import { MethodMarks } from '../method-marks';
import { MarkedProxyDecorator } from '../proxy.decorator';
import { CacheHit, CacheStore } from './cache-store';

const marks = new MethodMarks<MarkedOverrides>('shared-decorators:cachedMethods');
const invalidationMarks = new MethodMarks('shared-decorators:cacheInvalidatingMethods');

/**
 * Marks a method as safe to answer from a previous call's result, optionally with its own
 * lifetime, key, and a say in which results are worth keeping. {@link ResultCache} serves it
 * at the composition root.
 *
 * Mark a read whose caller can live with an answer up to `ttlMs` old. Leave it unmarked when
 * the caller needs the current value — a cache turns a correctness question into a staleness
 * one, and no configuration here can answer it for them.
 *
 * **Async only, enforced by the compiler**: reaching the store is itself asynchronous, so the
 * aspect cannot be transparent to a synchronous method. Prototype methods only.
 *
 * @example
 * class S3FileRepository implements FileRepository {
 *   @cached()
 *   public fetch(path: string): Promise<Buffer> { ... }
 *
 *   @cached({ ttlMs: 60_000, key: (folder: string) => folder })
 *   public list(folder: string): Promise<string[]> { ... }
 *
 *   @cached({ unless: (found: string[]) => found.length === 0 })
 *   public search(term: string): Promise<string[]> { ... }
 *
 *   @invalidatesCache()
 *   public write(path: string, content: Buffer): Promise<void> { ... }
 * }
 *
 * @template TArgs the marked method's parameters, inferred from `key` — supplying a key
 *   therefore also checks it against the method it is applied to
 * @template TResult the marked method's resolved value, inferred from `unless`, and checked
 *   against the method the same way
 */
export function cached<TArgs extends unknown[] = never[], TResult = unknown>(
  overrides?: CacheOverrides<TArgs, TResult>
) {
  if (overrides?.ttlMs !== undefined && !(overrides.ttlMs > 0)) {
    throw new Error('[cached] ttlMs must be greater than 0');
  }

  // `descriptor` carries the `T` constraint — the only place a legacy method decorator sees
  // the method's type. Returned unchanged.
  return <T extends (...args: TArgs) => Promise<TResult>>(
    prototype: object,
    propertyKey: string | symbol,
    descriptor: TypedPropertyDescriptor<T>
  ): TypedPropertyDescriptor<T> => {
    marks.add(prototype, propertyKey, overrides as MarkedOverrides);

    return descriptor;
  };
}

/**
 * Marks a method as clearing the store once it settles.
 *
 * The counterpart to {@link cached}: mark the writes, so a caller that reads its own write
 * back is not answered from an entry the write has just made wrong. The whole store goes,
 * because only the target knows which reads a write affects and dropping too many costs a
 * call rather than a correct answer.
 *
 * Accepts a synchronous method, unlike {@link cached}: it adds nothing to the return.
 */
export function invalidatesCache(): MethodDecorator {
  return (prototype, propertyKey) => invalidationMarks.add(prototype, propertyKey);
}

/**
 * The methods marked with {@link cached} on `target` or up its prototype chain, mapped to
 * their overrides. Empty when nothing is marked.
 *
 * A delegating function rather than `= marks.of`: detaching a method loses `this`.
 */
export function cachedMethodsOf(target: object): ReadonlyMap<string, MarkedOverrides | undefined> {
  return marks.of(target);
}

/** The methods marked with {@link invalidatesCache} on `target` or up its prototype chain. */
export function cacheInvalidatingMethodsOf(target: object): ReadonlyMap<string, void | undefined> {
  return invalidationMarks.of(target);
}

/** How long a result may be served for. */
export interface CachePolicy {
  /** Default lifetime of an entry, in milliseconds. Must be > 0. */
  ttlMs: number;
}

/**
 * Per-method overrides on top of the instance {@link CachePolicy}.
 *
 * @template TArgs the marked method's parameters, inferred from `key`
 * @template TResult the marked method's resolved value, inferred from `unless`
 */
export interface CacheOverrides<TArgs extends unknown[] = never[], TResult = unknown> {
  /** Lifetime for this method's entries, in place of the instance default. Must be > 0. */
  ttlMs?: CachePolicy['ttlMs'];

  /**
   * Builds this method's key from the call's arguments, in place of the default JSON of them.
   * Supply one whenever an argument is not small, plain data: the default cannot key on a
   * cyclic argument at all, cannot tell `undefined` from `null`, and would hold a whole
   * buffer in memory as a key.
   *
   * The key is namespaced by method name already.
   */
  key?: (...args: TArgs) => string;

  /**
   * Decides, from the result, that this call is not worth remembering. Nothing is written
   * when it returns `true`, and the next caller reaches the downstream again.
   *
   * For a result whose emptiness says "not yet" rather than "there is none": an absence is
   * the likeliest of answers to change, and a caller reading one back for the whole `ttlMs`
   * cannot tell the two apart. It buys nothing against a stale *non-empty* result, which
   * only invalidation can address.
   *
   * Annotate the parameter — inference has nowhere else to read `TResult` from.
   */
  unless?: (result: TResult) => boolean;
}

/** The key builder as the proxy calls it — the call site types it against the method's parameters. */
type KeyBuilder = (...args: unknown[]) => string;

/** The predicate as the proxy calls it — the call site types it against the method's result. */
type ResultPredicate = (result: unknown) => boolean;

/** Overrides as a mark holds them: a {@link KeyBuilder} for the key, the proxy having no argument types. */
export type MarkedOverrides = CacheOverrides<unknown[]>;

/**
 * ResultCache
 *
 * A proxy-based decorator that answers a target's {@link cached} methods from a
 * {@link CacheStore}, for `policy.ttlMs`, sparing a downstream the calls it would have
 * answered identically. Unmarked methods are returned untouched.
 *
 * **The store is the boundary between targets.** Two targets sharing one keep answering each
 * other's calls, so use one ResultCache and one store per target.
 *
 * **A failure is never cached**: nothing is written until the call resolves. And a store that
 * throws is a miss, so a cache outage costs calls rather than failing them — reporting that
 * failure is the store's own business.
 *
 * **The lifetime runs from the call, not from its answer**, so no caller is served a result
 * older than `ttlMs` and a slow call's entry lives correspondingly shorter.
 *
 * **Single flight is not offered.** Concurrent callers on a cold key each reach the
 * downstream, and the last one written wins; coalescing them could only ever hold within one
 * process anyway.
 *
 * **Compose it outside {@link Retrier}, {@link DeadlineEnforcer} and {@link RateLimiter}**, so
 * a hit spends no attempts, no budget and no tokens — the point of the cache is that these
 * never run. That also makes a {@link CallMeter} inside it count misses only, which against
 * one outside it gives the hit rate.
 *
 * @example
 * const cache = new ResultCache(assetCacheStore, { ttlMs: 30_000 });
 *
 * // innermost-first: translate, log, pace, bound, retry, then serve from cache
 * export const s3Standard = new ProxyPipeline().use(translateS3Error, logCalls, rateLimit, deadline, retry, cache);
 */
export class ResultCache extends MarkedProxyDecorator<MarkedOverrides> {
  /** Bumped by every invalidation, so a call that outlived one cannot write its result back. */
  private generation = 0;

  constructor(
    private readonly store: CacheStore,
    private readonly policy: CachePolicy
  ) {
    super(marks);

    if (!(policy.ttlMs > 0)) throw new Error('[ResultCache] ttlMs must be greater than 0');
  }

  /**
   * Applies the invalidation gate, then defers to the {@link cached} one. The two marks are
   * counterparts and never meet on a method; a method carrying both invalidates only.
   */
  protected wrap(
    method: (...args: unknown[]) => unknown,
    methodName: string,
    target: object
  ): (...args: unknown[]) => unknown {
    if (invalidationMarks.of(target).has(methodName)) return this.invalidating(method);

    return super.wrap(method, methodName, target);
  }

  protected wrapMarked(
    method: (...args: unknown[]) => unknown,
    methodName: string,
    overrides: MarkedOverrides | undefined
  ): (...args: unknown[]) => unknown {
    const ttlMs = overrides?.ttlMs ?? this.policy.ttlMs;
    const keyOf = overrides?.key;
    const skip = overrides?.unless;

    return async (...args: unknown[]) => {
      const key = this.keyFor(methodName, args, keyOf);

      // Serving the call is a call spent; guessing a key would answer it from another's entry.
      if (key === undefined) return method(...args);

      const generation = this.generation;
      const hit = await this.lookup(key);

      if (hit) return hit.value;

      const startedMs = Date.now();
      const value = await method(...args);

      if (this.worthKeeping(value, skip)) await this.remember(key, value, ttlMs, startedMs, generation);

      return value;
    };
  }

  private invalidating(method: (...args: unknown[]) => unknown): (...args: unknown[]) => unknown {
    return (...args: unknown[]) => {
      const result = method(...args);

      // Failures included: a write that reported one may still have landed.
      if (!(result instanceof Promise)) {
        void this.clear();

        return result;
      }

      return result.finally(() => this.clear());
    };
  }

  private async lookup(key: string): Promise<CacheHit | undefined> {
    try {
      return await this.store.get(key);
    } catch {
      // A store that cannot answer is a miss, never a failed call.
      return undefined;
    }
  }

  private worthKeeping(value: unknown, skip: ResultPredicate | undefined): boolean {
    if (!skip) return true;

    try {
      return !skip(value);
    } catch {
      // A predicate that cannot decide is no grounds for a `ttlMs`-long entry, nor for a failed call.
      return false;
    }
  }

  private async remember(
    key: string,
    value: unknown,
    ttlMs: number,
    startedMs: number,
    generation: number
  ): Promise<void> {
    const remainingMs = ttlMs - (Date.now() - startedMs);

    // Nothing left to serve it for, or a write invalidated the store while the call was out
    // and this result predates it.
    if (remainingMs <= 0 || generation !== this.generation) return;

    try {
      await this.store.set(key, value, remainingMs);
    } catch {
      // A store that cannot keep it is not a failed call.
    }
  }

  private async clear(): Promise<void> {
    this.generation += 1;

    try {
      await this.store.clear();
    } catch {
      // Nothing to do but leave the entries to expire on their own.
    }
  }

  private keyFor(methodName: string, args: unknown[], keyOf: KeyBuilder | undefined): string | undefined {
    try {
      return `${methodName}:${keyOf ? keyOf(...args) : JSON.stringify(args)}`;
    } catch {
      // A cyclic argument, a bigint, or a `key` that threw. None is a reason to fail the call.
      return undefined;
    }
  }
}
